import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, CheckCircle2, Shield, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/parent/link")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Link a child — MAKAI" }] }),
  component: LinkChild,
});

interface LinkedChild { learner_id: string; full_name: string; grade: string | null; }

function LinkChild() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [children, setChildren] = useState<LinkedChild[]>([]);

  async function load() {
    const { data: links } = await supabase.from("parent_links").select("learner_id");
    if (!links?.length) { setChildren([]); return; }
    const ids = links.map((l) => l.learner_id);
    const { data: profiles } = await supabase.from("profiles").select("id, full_name, grade").in("id", ids);
    setChildren((profiles ?? []).map((p) => ({ learner_id: p.id, full_name: p.full_name, grade: p.grade })));
  }

  useEffect(() => { load(); }, []);

  async function link(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 8) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("link_child_by_code", { _code: code.toUpperCase() });
      if (error) throw error;
      toast.success("Child linked!");
      setCode("");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not link child.");
    } finally { setBusy(false); }
  }

  async function unlink(learnerId: string) {
    const { error } = await supabase.from("parent_links").delete().eq("learner_id", learnerId);
    if (error) { toast.error(error.message); return; }
    toast.success("Child unlinked");
    await load();
  }

  return (
    <AppShell role="parent" title="Link a child" subtitle="Connect to your child's MAKAI account.">
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary-soft text-primary">
            <Link2 className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold">Enter your child's link code</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ask your child to open MAKAI → their dashboard shows an 8-character link code they can read to you.
          </p>

          <form className="mt-5 space-y-4" onSubmit={link}>
            <div className="space-y-1.5">
              <Label htmlFor="code">Link code</Label>
              <Input
                id="code"
                maxLength={8}
                placeholder="A2B4C6D8"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                className="text-center font-display text-2xl tracking-[0.4em] uppercase"
              />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={code.length !== 8 || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link child account"}
            </Button>
          </form>

          {children.length > 0 && (
            <div className="mt-6 space-y-2">
              <div className="text-sm font-semibold">Linked children</div>
              {children.map((c) => (
                <div key={c.learner_id} className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <div>
                      <div className="text-sm font-semibold">{c.full_name}</div>
                      <div className="text-xs text-muted-foreground">{c.grade ?? "Learner"}</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => unlink(c.learner_id)} aria-label="Unlink">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-sky/15 text-sky">
            <Shield className="h-6 w-6" />
          </div>
          <h3 className="mt-4 font-display text-lg font-bold">Privacy & safety</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>• You only see learning activity — never chats or private notes.</li>
            <li>• Your child can unlink you at any time.</li>
            <li>• Multiple parents/guardians can be linked to one child.</li>
            <li>• Codes are unique 8-character strings generated at signup.</li>
          </ul>
        </div>
      </div>
    </AppShell>
  );
}
